/**
 * Inline Status cell for the Cards / Sealed inventory tables.
 *
 * Available <-> Listed is the only status pair a vendor flips by hand, so
 * those render as a pill-styled <select> editable in place. Everything else
 * (At Grading, Repair, Sold, Traded, Opened) is machine-managed by the
 * grading / sale / trade / rip flows and renders as a read-only pill.
 */
import { DB } from "../lib/db";
import { escapeHtml, toast } from "../lib/dom";
import { render } from "../state";

const PILL: Record<string, string> = {
  "Available":  "pill-green",
  "Listed":     "pill-blue",
  "At Grading": "pill-amber",
  "Repair":     "pill-amber",
  "Sold":       "pill-gray",
  "Traded":     "pill-gray",
  "Opened":     "pill-violet",
  "Gifted":     "pill-gray",
  "Written Off": "pill-red",
};
const FLIPPABLE = ["Available", "Listed"];

export function statusPillClass(status: string): string {
  return PILL[status] || "pill-gray";
}

export function statusCellHtml(table: "cards" | "sealed", lotId: string, status: string): string {
  const s = status || "Available";
  const cls = statusPillClass(s);
  if (!FLIPPABLE.includes(s)) {
    return `<span class="pill ${cls}" title="Set automatically by the ${s === "At Grading" || s === "Repair" ? "grading" : "sale / trade / rip"} flow">${escapeHtml(s)}</span>`;
  }
  return `<select class="pill ${cls} status-select" title="Flip between Available and Listed \u2014 other states are set automatically by sales, trades, grading, and rips"
    onchange="updateLotStatus('${table}','${escapeHtml(lotId)}',this.value)" onclick="event.stopPropagation()">
    ${FLIPPABLE.map(o => `<option value="${o}" ${o === s ? "selected" : ""}>${o}</option>`).join("")}
  </select>`;
}

/** Commit an inline Available/Listed flip straight to the DB. */
export function updateLotStatus(table: "cards" | "sealed", lotId: string, value: string): void {
  if (!FLIPPABLE.includes(value)) return;
  const keyField = table === "cards" ? "Card Lot ID" : "Lot ID";
  try {
    DB.update(table, keyField, lotId, { "Status": value });
  } catch (e: any) {
    console.error("[updateLotStatus]", e);
    toast("Status update failed: " + (e?.message || e), "err");
    return;
  }
  toast(`${lotId} \u2192 ${value}`);
  render();
}
(window as any).updateLotStatus = updateLotStatus;

/** Bulk Available/Listed flip for the selection toolbar. Machine-managed
 *  states (At Grading, Sold, Traded…) are skipped and counted in the toast. */
export function bulkSetStatus(table: "cards" | "sealed", ids: string[], value: string): void {
  if (!FLIPPABLE.includes(value)) return;
  const keyField = table === "cards" ? "Card Lot ID" : "Lot ID";
  let changed = 0, skipped = 0;
  for (const id of ids) {
    const row = DB.table(table).find(r => r[keyField] === id);
    if (!row) continue;
    const cur = String(row["Status"] || "Available");
    if (!FLIPPABLE.includes(cur)) { skipped++; continue; }
    if (cur === value) continue;
    DB.update(table, keyField, id, { "Status": value });
    changed++;
  }
  const parts = [`${changed} marked ${value}`];
  if (skipped) parts.push(`${skipped} skipped (machine-managed status)`);
  toast(parts.join(" \u00b7 "));
  render();
}
